/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalPrivilegeHistoryListResponse } from '../models/clinical-privilege-history-list-response';
import { ClinicalPrivilegeItemResponse } from '../models/clinical-privilege-item-response';
import { ClinicalPrivilegeListResponse } from '../models/clinical-privilege-list-response';
import { CreateClinicalPrivilegeRequest } from '../models/create-clinical-privilege-request';
import { UpdateClinicalPrivilegeRequest } from '../models/update-clinical-privilege-request';

/**
 * Privilege skill-row CRUD under a privilege heading (activate/deactivate + history).
 */
@Injectable({ providedIn: 'root' })
export class ClinicalPrivilegeCatalogService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `listClinicalPrivileges()` */
  static readonly ListClinicalPrivilegesPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges';

  /**
   * List privileges under a privilege category.
   *
   * **Sortable columns** — use `sort=column,asc` or `sort=column,desc`:
   * - `sortOrder` (default)
   * - `label`, `code`, `active`, `createdAt`, `updatedAt`, `id`
   *
   * **Search** — partial match, not case-sensitive:
   * - `label`, `code`
   *
   * **Filters:**
   * - `activeOnly` — `true` (default) returns active only; `false` returns active and inactive
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClinicalPrivileges()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalPrivileges$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      activeOnly?: boolean;

      /**
       * Search privilege label or code.
       */
      search?: string;

      /**
       * Sort order. Format: column,asc or column,desc. Default: sortOrder,asc.
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.ListClinicalPrivilegesPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.query('activeOnly', params.activeOnly, {});
      rb.query('search', params.search, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeListResponse>;
        })
      );
  }

  /**
   * List privileges under a privilege category.
   *
   * **Sortable columns** — use `sort=column,asc` or `sort=column,desc`:
   * - `sortOrder` (default)
   * - `label`, `code`, `active`, `createdAt`, `updatedAt`, `id`
   *
   * **Search** — partial match, not case-sensitive:
   * - `label`, `code`
   *
   * **Filters:**
   * - `activeOnly` — `true` (default) returns active only; `false` returns active and inactive
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listClinicalPrivileges$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalPrivileges(
    params: {
      id: number;
      privilegeCategoryId: number;
      activeOnly?: boolean;

      /**
       * Search privilege label or code.
       */
      search?: string;

      /**
       * Sort order. Format: column,asc or column,desc. Default: sortOrder,asc.
       */
      sort?: string;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeListResponse> {
    return this.listClinicalPrivileges$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeListResponse>
        ): ClinicalPrivilegeListResponse => r.body
      )
    );
  }

  /** Path part for operation `createClinicalPrivilege()` */
  static readonly CreateClinicalPrivilegePath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges';

  /**
   * Create privilege.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClinicalPrivilege()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalPrivilege$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      body: CreateClinicalPrivilegeRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.CreateClinicalPrivilegePath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeItemResponse>;
        })
      );
  }

  /**
   * Create privilege.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClinicalPrivilege$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClinicalPrivilege(
    params: {
      id: number;
      privilegeCategoryId: number;
      body: CreateClinicalPrivilegeRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeItemResponse> {
    return this.createClinicalPrivilege$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeItemResponse>
        ): ClinicalPrivilegeItemResponse => r.body
      )
    );
  }

  /** Path part for operation `getClinicalPrivilege()` */
  static readonly GetClinicalPrivilegePath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges/{privilegeId}';

  /**
   * Get privilege.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClinicalPrivilege()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClinicalPrivilege$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.GetClinicalPrivilegePath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.path('privilegeId', params.privilegeId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeItemResponse>;
        })
      );
  }

  /**
   * Get privilege.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getClinicalPrivilege$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClinicalPrivilege(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeItemResponse> {
    return this.getClinicalPrivilege$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeItemResponse>
        ): ClinicalPrivilegeItemResponse => r.body
      )
    );
  }

  /** Path part for operation `updateClinicalPrivilege()` */
  static readonly UpdateClinicalPrivilegePath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges/{privilegeId}';

  /**
   * Update privilege.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClinicalPrivilege()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalPrivilege$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
      body: UpdateClinicalPrivilegeRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.UpdateClinicalPrivilegePath,
      'put'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.path('privilegeId', params.privilegeId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeItemResponse>;
        })
      );
  }

  /**
   * Update privilege.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClinicalPrivilege$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClinicalPrivilege(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
      body: UpdateClinicalPrivilegeRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeItemResponse> {
    return this.updateClinicalPrivilege$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeItemResponse>
        ): ClinicalPrivilegeItemResponse => r.body
      )
    );
  }

  /** Path part for operation `activateClinicalPrivilege()` */
  static readonly ActivateClinicalPrivilegePath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges/{privilegeId}/activate';

  /**
   * Activate privilege.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `activateClinicalPrivilege()` instead.
   *
   * This method doesn't expect any request body.
   */
  activateClinicalPrivilege$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.ActivateClinicalPrivilegePath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.path('privilegeId', params.privilegeId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeItemResponse>;
        })
      );
  }

  /**
   * Activate privilege.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `activateClinicalPrivilege$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  activateClinicalPrivilege(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeItemResponse> {
    return this.activateClinicalPrivilege$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeItemResponse>
        ): ClinicalPrivilegeItemResponse => r.body
      )
    );
  }

  /** Path part for operation `deactivateClinicalPrivilege()` */
  static readonly DeactivateClinicalPrivilegePath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges/{privilegeId}/deactivate';

  /**
   * Deactivate privilege.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deactivateClinicalPrivilege()` instead.
   *
   * This method doesn't expect any request body.
   */
  deactivateClinicalPrivilege$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.DeactivateClinicalPrivilegePath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.path('privilegeId', params.privilegeId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeItemResponse>;
        })
      );
  }

  /**
   * Deactivate privilege.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deactivateClinicalPrivilege$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deactivateClinicalPrivilege(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeItemResponse> {
    return this.deactivateClinicalPrivilege$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeItemResponse>
        ): ClinicalPrivilegeItemResponse => r.body
      )
    );
  }

  /** Path part for operation `getClinicalPrivilegeHistory()` */
  static readonly GetClinicalPrivilegeHistoryPath =
    '/api/v1/clinical-privileges/formcategories/{id}/privilege-categories/{privilegeCategoryId}/privileges/{privilegeId}/history';

  /**
   * Privilege history.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClinicalPrivilegeHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClinicalPrivilegeHistory$Response(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalPrivilegeHistoryListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalPrivilegeCatalogService.GetClinicalPrivilegeHistoryPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('privilegeCategoryId', params.privilegeCategoryId, {});
      rb.path('privilegeId', params.privilegeId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalPrivilegeHistoryListResponse>;
        })
      );
  }

  /**
   * Privilege history.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getClinicalPrivilegeHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClinicalPrivilegeHistory(
    params: {
      id: number;
      privilegeCategoryId: number;
      privilegeId: number;
    },
    context?: HttpContext
  ): Observable<ClinicalPrivilegeHistoryListResponse> {
    return this.getClinicalPrivilegeHistory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalPrivilegeHistoryListResponse>
        ): ClinicalPrivilegeHistoryListResponse => r.body
      )
    );
  }
}
